/**
 * Voice controller bound to `SessionContext` (T6, docs/ARCHITECTURE.md §9). Fetches scoped credentials for
 * this participant from the server, picks the mock or LiveKit client by `session.voice.provider`, and keeps
 * exactly one voice connection alive across the whole session: called once at the top of `SessionSurface`
 * (see `SessionPage.tsx`), it stays mounted while `Lobby`/`PlaySurface`/`Results` swap underneath it, so the
 * shared room is never dropped and reconnected on a lobby<->board<->activity transition.
 *
 * Team-scoped voice: when `session.voice.rooms` gains a team room this viewer belongs to (a `teams`-scope
 * module instance is active), `selectRoomForViewer` picks it over the shared room and this hook reconnects to
 * it; when the instance ends and the team room disappears, it reconnects back to the shared room. Every
 * failure surfaces as `status`/`detail` text — never a thrown error, never gameplay-blocking.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VoiceClient, VoiceConnectionStatus, VoiceMode, VoiceProvider } from '@momentum/shared';
import { getVoiceCredentials } from '../api.ts';
import { useSessionContext } from '../session/SessionContext.ts';
import { createMockVoiceClient, type MockVoiceClient } from './mockVoiceClient.ts';
import { createLiveKitVoiceClient } from './livekitVoiceClient.ts';
import { roomsKey, selectRoomForViewer } from './rooms.ts';

export interface UseVoiceResult {
  mode: VoiceMode;
  status: VoiceConnectionStatus;
  detail: string | null;
  muted: boolean;
  toggleMuted: () => Promise<void>;
  /** Participant identities currently speaking (provider-driven, or the mock's simulated pattern). */
  speaking: ReadonlySet<string>;
  /** `null` only while voice is off for this room. */
  provider: VoiceProvider | null;
  /** True whenever no live provider is configured, or the session runs simulated participants. */
  simulated: boolean;
  /** Re-attempts fetching credentials and connecting to the currently selected room. */
  retry: () => void;
  /** False for spectators/presenters: they can watch the panel but the mic never turns on for them. */
  canPublish: boolean;
  /** Has this connection ever successfully unmuted? Drives the one-time "Enable microphone" CTA. */
  everUnmuted: boolean;
  /** Browser autoplay policy currently requires an explicit click before remote audio can play. */
  audioPlaybackBlocked: boolean;
  enableAudio: () => Promise<void>;
}

function createClientFor(provider: VoiceProvider): VoiceClient {
  return provider === 'livekit' ? createLiveKitVoiceClient() : createMockVoiceClient();
}

export function useVoice(): UseVoiceResult {
  const { sessionId, client, view, self, credentials } = useSessionContext();
  const session = view.session;
  const mode = session.voice.mode;
  const provider = session.voice.provider;
  const rooms = session.voice.rooms;
  const participantId = self?.participantId ?? null;
  const teamId = self?.teamId ?? null;
  const canPublish = self?.role === 'player';
  const presenterSession = session.settings.presenter;

  const [status, setStatusState] = useState<VoiceConnectionStatus>('idle');
  const [detail, setDetailState] = useState<string | null>(null);
  const [muted, setMutedState] = useState(true);
  const [everUnmuted, setEverUnmuted] = useState(false);
  const [audioPlaybackBlocked, setAudioPlaybackBlocked] = useState(false);
  const [speaking, setSpeaking] = useState<ReadonlySet<string>>(new Set());
  const [retryTick, setRetryTick] = useState(0);

  const voiceClientRef = useRef<VoiceClient | null>(null);
  const connectedRoomRef = useRef<string | null>(null);
  const desiredMutedRef = useRef(true);
  const handledRetryRef = useRef(0);

  // One client per (provider, mode!=off), created once and torn down only when voice turns off entirely or
  // the session ends — never on the lobby<->board<->activity render swap, since this hook lives above both.
  useEffect(() => {
    if (mode === 'off') return undefined;
    const voiceClient = createClientFor(provider);
    voiceClientRef.current = voiceClient;
    connectedRoomRef.current = null;
    desiredMutedRef.current = true;
    setEverUnmuted(false);

    const unsubscribeStatus = voiceClient.onStatus((nextStatus, nextDetail) => {
      setStatusState(nextStatus);
      setDetailState(nextDetail ?? null);
      setAudioPlaybackBlocked(voiceClient.isAudioPlaybackBlocked?.() ?? false);
    });
    const unsubscribeSpeaking = voiceClient.onSpeaking((identities) => {
      setSpeaking(new Set(identities));
    });

    return () => {
      unsubscribeStatus();
      unsubscribeSpeaking();
      voiceClientRef.current = null;
      setAudioPlaybackBlocked(false);
      voiceClient.disconnect().catch(() => {
        // Best-effort teardown; nothing else observes this session's voice client any more.
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- provider/mode are fixed for a session's lifetime.
  }, [mode, provider]);

  const roomsFingerprint = useMemo(() => roomsKey(rooms), [rooms]);
  const simulatedRoster = useMemo(
    () => session.participants.filter((p) => p.simulated).map((p) => p.id),
    [session.participants],
  );

  // Connect (or reconnect, on a team<->shared room switch, or an explicit retry) whenever the target room or
  // membership changes. Fetching credentials happens here, right before connecting, so a stale credential from
  // an earlier room is never reused.
  useEffect(() => {
    if (mode === 'off' || !participantId) return undefined;
    const voiceClient = voiceClientRef.current;
    if (!voiceClient) return undefined;

    const targetRoom = selectRoomForViewer(rooms, participantId, teamId);
    const retryRequested = retryTick !== handledRetryRef.current;
    if (!targetRoom) return undefined;
    if (targetRoom.roomName === connectedRoomRef.current && !retryRequested) return undefined;

    let cancelled = false;

    (async () => {
      handledRetryRef.current = retryTick;
      if (connectedRoomRef.current !== null) {
        try {
          await voiceClient.disconnect();
        } catch {
          // Falling through to reconnect regardless.
        }
      }
      try {
        const { credentials: creds } = await getVoiceCredentials(sessionId, credentials.token);
        if (cancelled) return;
        await voiceClient.connect(creds);
        if (cancelled) return;
        connectedRoomRef.current = targetRoom.roomName;
        await voiceClient.setMuted(desiredMutedRef.current);
        setMutedState(voiceClient.isMuted());
      } catch (err) {
        if (cancelled) return;
        connectedRoomRef.current = null;
        setStatusState('failed');
        setDetailState(err instanceof Error ? err.message : 'Could not fetch voice credentials');
      }
    })();

    return () => {
      cancelled = true;
    };
    // `rooms` itself is intentionally omitted: it is a new array reference on every session snapshot, but
    // `roomsFingerprint` already captures every change to its content that should trigger a reconnect.
  }, [mode, participantId, teamId, roomsFingerprint, retryTick, sessionId, credentials.token]);

  // Presenter fixtures only: keeps the mock's simulated speaking pattern in sync with the current simulated
  // roster, without needing a reconnect (docs/ARCHITECTURE.md §10, T6 §5).
  useEffect(() => {
    const voiceClient = voiceClientRef.current;
    if (!voiceClient || voiceClient.provider !== 'mock') return;
    (voiceClient as MockVoiceClient).setSimulation(presenterSession, simulatedRoster);
  }, [presenterSession, simulatedRoster, status]);

  const toggleMuted = useCallback(async () => {
    const voiceClient = voiceClientRef.current;
    if (!voiceClient || !canPublish) return;
    const next = !muted;
    desiredMutedRef.current = next;
    await voiceClient.setMuted(next);
    const actual = voiceClient.isMuted();
    setMutedState(actual);
    if (!actual) setEverUnmuted(true);
    void client.send('session.set_muted', { muted: actual }, 'session');
  }, [canPublish, muted, client]);

  const retry = useCallback(() => {
    setRetryTick((tick) => tick + 1);
  }, []);

  const enableAudio = useCallback(async () => {
    const voiceClient = voiceClientRef.current;
    if (!voiceClient?.startAudio) return;
    await voiceClient.startAudio();
    setAudioPlaybackBlocked(voiceClient.isAudioPlaybackBlocked?.() ?? false);
  }, []);

  return {
    mode,
    status: mode === 'off' ? 'idle' : status,
    detail,
    muted,
    toggleMuted,
    speaking,
    provider: mode === 'off' ? null : provider,
    simulated: session.voice.simulated,
    retry,
    canPublish,
    everUnmuted,
    audioPlaybackBlocked,
    enableAudio,
  };
}
